import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  BIRD_EYE_DRAG_MIME,
  type BirdEyeDragSource,
  type BirdEyeDropTarget,
} from '../../bird-eye/boardTypes';

function targetsEqual(a: BirdEyeDropTarget | null, b: BirdEyeDropTarget | null): boolean {
  if (!a || !b) return false;
  if (a.type !== b.type) return false;
  if (a.type === 'list' && b.type === 'list') return a.lane === b.lane;
  if (a.type === 'operatory' && b.type === 'operatory') return a.index === b.index;
  return false;
}

interface BirdEyeDndContextValue {
  dragging: BirdEyeDragSource | null;
  dragOver: BirdEyeDropTarget | null;
  isDropActive: (target: BirdEyeDropTarget) => boolean;
  isDraggingSource: (source: BirdEyeDragSource) => boolean;
  onDragStart: (source: BirdEyeDragSource, e: React.DragEvent) => void;
  onDragEnd: () => void;
  bindDropZone: (target: BirdEyeDropTarget) => {
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

const BirdEyeDndContext = createContext<BirdEyeDndContextValue | null>(null);

export function useBirdEyeDnd(): BirdEyeDndContextValue {
  const ctx = useContext(BirdEyeDndContext);
  if (!ctx) throw new Error('useBirdEyeDnd must be used within BirdEyeDndProvider');
  return ctx;
}

export interface BirdEyeDndProviderProps {
  onMove: (from: BirdEyeDragSource, to: BirdEyeDropTarget) => void;
  children: React.ReactNode;
}

export function BirdEyeDndProvider({ onMove, children }: BirdEyeDndProviderProps) {
  const [dragging, setDragging] = useState<BirdEyeDragSource | null>(null);
  const [dragOver, setDragOver] = useState<BirdEyeDropTarget | null>(null);

  const onDragStart = useCallback((source: BirdEyeDragSource, e: React.DragEvent) => {
    setDragging(source);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(BIRD_EYE_DRAG_MIME, JSON.stringify(source));
    if (e.currentTarget instanceof HTMLElement) {
      e.dataTransfer.setDragImage(e.currentTarget, 24, 20);
    }
  }, []);

  const onDragEnd = useCallback(() => {
    setDragging(null);
    setDragOver(null);
  }, []);

  const isDropActive = useCallback(
    (target: BirdEyeDropTarget) => targetsEqual(dragOver, target),
    [dragOver],
  );

  const isDraggingSource = useCallback(
    (source: BirdEyeDragSource) => {
      if (!dragging) return false;
      if (dragging.type !== source.type) return false;
      if (dragging.type === 'list' && source.type === 'list') {
        return dragging.lane === source.lane && dragging.patientId === source.patientId;
      }
      if (dragging.type === 'operatory' && source.type === 'operatory') {
        return dragging.index === source.index;
      }
      return false;
    },
    [dragging],
  );

  const bindDropZone = useCallback(
    (target: BirdEyeDropTarget) => ({
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(target);
      },
      onDragEnter: (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(target);
      },
      onDragLeave: (e: React.DragEvent) => {
        const related = e.relatedTarget as Node | null;
        if (related && e.currentTarget.contains(related)) return;
        setDragOver((prev) => (targetsEqual(prev, target) ? null : prev));
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(null);
        setDragging(null);
        const raw = e.dataTransfer.getData(BIRD_EYE_DRAG_MIME);
        if (!raw) return;
        try {
          const from = JSON.parse(raw) as BirdEyeDragSource;
          onMove(from, target);
        } catch {
          /* ignore malformed payload */
        }
      },
    }),
    [onMove],
  );

  const value = useMemo(
    () => ({
      dragging,
      dragOver,
      isDropActive,
      isDraggingSource,
      onDragStart,
      onDragEnd,
      bindDropZone,
    }),
    [dragging, dragOver, isDropActive, isDraggingSource, onDragStart, onDragEnd, bindDropZone],
  );

  return <BirdEyeDndContext.Provider value={value}>{children}</BirdEyeDndContext.Provider>;
}
